// React and hooks
import { useState } from "react";

// Third-party libraries
import AnimateHeight from "react-animate-height";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faCircleExclamation, faShieldCheck } from "@fortawesome/pro-solid-svg-icons";

// Static data
import { CountryList } from "../../../../assets/staticData/CountryList";

// Types
import { AnswerListOverviewDto, KeldanKYCReport, PoliticalDanger } from "../../../../types/CompanyAnswersTypes";

// UI elements
import Alert from "../../../../ui-elements/Alert/Alert";
import DisplayBox from "../../../../ui-elements/DisplayBox/DisplayBox";
import Table, { IColumn } from "../../../../ui-elements/Table/Table";

// Utilities
import { insertDash, getTableDate, getTimeFromDate } from "../../../../services/utils";

// Constants
import { getCompanyQuestionDescription } from "../Constants";
import Link from "../../../../ui-elements/Link/Link";
import { getFileIcon } from "../../../../ui-elements/FileUploadDropZone/FileUploadDropZone";

interface IProps {
    data: AnswerListOverviewDto | null;
    answerListIndex: number;
}
const CompanyAnswers: React.FC<IProps> = ({ data, answerListIndex }) => {
    const userFiles = data?.answerLists[answerListIndex].userFiles || [];

    const renderAnswer = (isWrong: boolean, answerBoolean: boolean | null, answerText: string, content?: JSX.Element) => {
        let text = '';
        if (answerBoolean) text = 'Já';
        else if (answerBoolean === false) text = 'Nei';

        if (answerText?.length > 0) {
            if (text === '') {
                text = answerText;
            } else {
                text = text + ', ' + answerText;
            }
        }
        return (
            <div className="answer-wrapper">
                <div className="answer-wrapper__text">
                    {isWrong &&
                        <FontAwesomeIcon className="fa-icon" icon={faCircleExclamation} />
                    }
                    <p className="paragraph-small--bold answer-text">
                        {text}
                    </p>
                </div>
                {content && <div className="answer-wrapper__content">
                    {content}
                </div>}
            </div>
        );
    }

    return <>
        <DisplayBox
            id="company-answers"
            title={data?.customerName ? `Svör við áreiðanleikakönnun hjá ${data.customerName}` : "Svör við áreiðanleikakönnun"}
        >
            <div className="content">
                {data?.answerLists[answerListIndex].userELoginTimestamp &&
                    <Alert
                        type={"success"}
                        iconOverWrite={faShieldCheck}
                        headText={`${data?.answerLists[answerListIndex].userName}, kt. ${insertDash(data?.answerLists[answerListIndex].userSSN ?? '')}`}
                        text={`Svaraði þessari könnun með rafrænni innskráningu í gegnum ${data?.answerLists[answerListIndex].islandIsLogin ? 'island.is' : 'Auðkenni'} þann 
                        ${getTableDate(data?.answerLists[answerListIndex].userELoginTimestamp, 'DD/MM/YYYY', '.')} 
                        kl. ${getTimeFromDate(data?.answerLists[answerListIndex].userELoginTimestamp, 'HH:MM:SS')} `}
                        expandedText={data?.answerLists[answerListIndex].audkenniTrackId ?
                            <div>
                                <p>
                                    Track ID: {data?.answerLists[answerListIndex].audkenniTrackId}<br />
                                    Veitt af: Auðkenni hf.<br />
                                </p>
                            </div> : undefined
                        }
                    />
                }
                <div className="answers-wrapper">
                    <div className="answers-header">
                        <h5 id="questions-overview">Spurningar</h5>
                        <h5 id="answers-overview">Svör</h5>
                    </div>
                    {data?.answerLists[answerListIndex].answers.map((item, index) => (
                        <div key={index} className='question-item'>
                            <div className="question-content">
                                <p className="paragraph-small--bold question-index">{item.order}.</p>
                                <div className="question-texts">
                                    <p className="paragraph-small--bold question-title">{item.question}</p>
                                    {getCompanyQuestionDescription(item.order, data.serviceCompanyName) && (
                                        <p className="paragraph-small--italic question-description">
                                            {getCompanyQuestionDescription(item.order, data.serviceCompanyName)}
                                        </p>
                                    )}
                                    {
                                        (item.order === 10 || item.order === 9) && <GetExtraAnswerOptionInfo data={data.keldanReport?.mappedReport} index={item.order} />
                                    }
                                </div>
                            </div>

                            {(item.order === 8)
                                ? renderAnswer(item.isWrong, item.answerBoolean, "", <PoliticalDangerTable data={item.politicalDanger} />)
                                : item.order === 1 || item.order === 4
                                    ? renderAnswer(item.isWrong, item.answerBoolean, CountryList.find(country => country.value === item.answertext)?.label || '')
                                    : renderAnswer(item.isWrong, item.answerBoolean, item.answertext)}
                        </div>
                    ))}
                </div>
                {userFiles && userFiles.length > 0 &&
                    (
                        <div className="uploaded-file-list">
                            <h5>Fylgiskjöl</h5>
                            {userFiles.map((file, index) => (
                                <div className='uploaded-file-list__item' key={index}>
                                    <div className='uploaded-file-list__item--left-section'>
                                        <p className='file-item__index'>{index + 1}.</p>
                                        <Link url={`/DueDiligence/GetFile/${file.id}`} icon={getFileIcon(file.fileExtensionType)} className='uploaded-file-list__name'>
                                            {file.encodedFileName}
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                }
            </div>
        </DisplayBox>
    </>
}
export default CompanyAnswers;

interface PoliticalDangerTableProps {
    data?: PoliticalDanger[];
}

const PoliticalDangerTable: React.FC<PoliticalDangerTableProps> = ({ data }) => {
    if (!data || data.length === 0) {
        return <></>
    }
    return <Table
        tableSize="lg"
        data={data}
        columns={[{
            title: 'Háttsett starf',
            renderCell: ({ job }) => job,
            textAlign: 'left',
        }, {
            title: 'Nafn',
            renderCell: ({ name }) => name,
            textAlign: 'left',
        }, {
            title: 'Kennitala',
            renderCell: ({ ssn }) => insertDash(ssn),
            textAlign: 'left',
        }]} />
}
interface IExtraOption {
    index: number;
    data?: KeldanKYCReport | null;
}
interface IFyrirtaekjaskraTable {
    kennitala: string;
    nafn: string;
    heimilisfang: string;
    stada?: string;
}

const GetExtraAnswerOptionInfo: React.FC<IExtraOption> = ({ index, data }) => {
    const [isOpen, setIsOpen] = useState(false);

    //fyrirtækjaskrá columns
    const fyrirtaekjaskraColumns: IColumn<IFyrirtaekjaskraTable>[] = [{
        title: 'Nafn',
        renderCell: ({ nafn }) => nafn,
        textAlign: 'left',
    }, {
        title: 'Kennitala',
        renderCell: ({ kennitala }) => insertDash(kennitala),
        textAlign: 'left',
    }, {
        title: 'Heimilisfang',
        renderCell: ({ heimilisfang }) => heimilisfang,
        textAlign: 'left',
    }];

    const stadaColumn: IColumn<IFyrirtaekjaskraTable> = {
        title: 'Staða',
        renderCell: ({ kennitala }) => {
            //find the stada from adilarfelags
            const adili = data?.adilarfelags?.find(adili => adili.kennadila === kennitala)
            if (adili === undefined) return '-';
            else if (adili.stodulys) {
                return adili.stodulys;
            } else if (adili.teglysing) {
                return adili.teglysing;
            }
            return '-';
        },
        textAlign: 'left'
    }

    let text = "Skoða raunverulega eigendur"
    if (index === 10) text = "Skoða upplýsingar úr fyrirtækjaskrá"

    const mapper = (kennitala: string, nafn: string, heimilisfang: string, stada?: string): IFyrirtaekjaskraTable => {
        return { kennitala, nafn, heimilisfang, stada };
    }
    return <div
        onClick={() => !isOpen && setIsOpen(true)}
        className={cx("extra-answer-option", { 'open': isOpen })}>
        <div className="extra-answer-option-info">
            <span
                className={cx("extra-answer-option-info-text", { 'open': isOpen })}
                onClick={() => setIsOpen(!isOpen)}
            >{text}</span>
            <FontAwesomeIcon
                icon={faAngleDown}
                className={cx("fa-icon", { 'open': isOpen })}
                onClick={() => setIsOpen(!isOpen)} />
        </div>
        <AnimateHeight
            duration={300}
            height={isOpen ? 'auto' : 0}
        >
            <div className="extra-answer-option-tables">
                {index === 10 && <>
                    <div>
                        <h5>Stjórn félags</h5>
                        <Table tableSize="lg" showEmptyTableText data={data?.stjorn.map(item => mapper(item.kennitala, item.nafn, item.heimili, item.tegRec)) ?? []} columns={[...fyrirtaekjaskraColumns, stadaColumn]} />
                    </div>
                    <div>
                        <h5>Endurskoðendur</h5>
                        <Table tableSize="lg" showEmptyTableText data={data?.endurskodendur.map(item => mapper(item.kennitala, item.nafn, item.heimili)) ?? []} columns={fyrirtaekjaskraColumns} />
                    </div>
                    <div>
                        <h5>Framkvæmdastjórn</h5>
                        <Table tableSize="lg" showEmptyTableText data={data?.framkvaemdarstjorar.map(item => mapper(item.kennitala, item.nafn, item.heimili)) ?? []} columns={fyrirtaekjaskraColumns} />
                    </div>
                    <div>
                        <h5>Prókúruumboð</h5>
                        <Table tableSize="lg" showEmptyTableText data={data?.prokuruhafar.map(item => mapper(item.kennitala, item.nafn, item.heimili)) ?? []} columns={fyrirtaekjaskraColumns} />
                    </div>
                    <div>
                        <h5>Stofnendur</h5>
                        <Table tableSize="lg" showEmptyTableText data={data?.stofnendur.map(item => mapper(item.kennitala, item.nafn, item.heimili)) ?? []} columns={fyrirtaekjaskraColumns} />
                    </div>
                    <div>
                        <h6>Tilgangur</h6>
                        <p className="paragraph-small">{data?.hlutafelag?.tilgangur ?? '-'}</p>
                    </div>
                    <div>
                        <h6>ÍSAT flokkun</h6>
                        <p className="paragraph-small">{data?.isatfelags && data?.isatfelags.length > 0 ? `${data?.isatfelags[0].isatkodi} - ${data?.isatfelags[0].lysing}` : '-'}</p>
                    </div>
                </>
                }
                {index === 9 && <>
                    <Table
                        showEmptyTableText
                        tableSize="lg"
                        data={data?.owners ?? []}
                        columns={[{
                            title: 'Nafn',
                            renderCell: ({ nafn }) => nafn,
                            textAlign: 'left',
                        }, {
                            title: 'Fæðingarár/mán.',
                            renderCell: ({ faedingarmanudur }) => faedingarmanudur ? faedingarmanudur.replace('-', '/') : '-',
                            textAlign: 'left',
                        }, {
                            title: 'Eignarhlutur',
                            renderCell: ({ eignarhlutur }) => eignarhlutur ? eignarhlutur : '-',
                        }]} />
                </>}
            </div>
        </AnimateHeight>
    </div>
}

